/**
 * Power Apps management tools (16 tools)
 * Covers Maker APIs, Admin APIs, and model-driven apps
 */

import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type { PowerAppsApi } from "../api/powerapps-api.js";
import { logger } from "../utils/logger.js";
import { ApiError, ConfirmationRequiredError } from "../utils/errors.js";

// ============================================================================
// Tool Definitions
// ============================================================================

export const listPowerAppsTool: Tool = {
  name: "list_powerapps",
  description: "List Power Apps canvas apps in an environment. Returns app names, IDs, owners, and last modified dates.",
  inputSchema: {
    type: "object",
    properties: {
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
  },
};

export const getPowerAppTool: Tool = {
  name: "get_powerapp",
  description: "Get detailed information about a Power App including owner, connections, and app URIs.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
    },
    required: ["appId"],
  },
};

export const deletePowerAppTool: Tool = {
  name: "delete_powerapp",
  description: "Delete a Power App permanently. This action cannot be undone. Set confirm=true to proceed.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID) to delete" },
      confirm: { type: "boolean", description: "Set to true to confirm deletion" },
    },
    required: ["appId"],
  },
};

export const publishPowerAppTool: Tool = {
  name: "publish_powerapp",
  description: "Publish a Power App to make the latest version available to users.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID) to publish" },
    },
    required: ["appId"],
  },
};

export const getPowerAppVersionsTool: Tool = {
  name: "get_powerapp_versions",
  description: "Get version history for a Power App. Shows all saved versions with dates.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
    },
    required: ["appId"],
  },
};

export const restorePowerAppVersionTool: Tool = {
  name: "restore_powerapp_version",
  description: "Restore a Power App to a previous version.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      versionId: { type: "string", description: "The version ID to restore" },
      confirm: { type: "boolean", description: "Set to true to confirm restore" },
    },
    required: ["appId", "versionId"],
  },
};

export const setPowerAppDisplayNameTool: Tool = {
  name: "set_powerapp_display_name",
  description: "Change the display name of a Power App.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      displayName: { type: "string", description: "New display name for the app" },
    },
    required: ["appId", "displayName"],
  },
};

export const getPowerAppPermissionsTool: Tool = {
  name: "get_powerapp_permissions",
  description: "Get the list of users, groups, and service principals that have access to a Power App.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
    },
    required: ["appId"],
  },
};

export const sharePowerAppTool: Tool = {
  name: "share_powerapp",
  description: "Share a Power App with a user, group, or service principal. Grant CanEdit or CanView permissions.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      principalId: { type: "string", description: "The user/group ID (Entra object ID)" },
      principalType: {
        type: "string",
        enum: ["User", "Group", "Tenant", "ServicePrincipal"],
        description: "Type of principal",
      },
      role: {
        type: "string",
        enum: ["CanEdit", "CanView"],
        description: "Permission level to grant",
      },
      tenantId: { type: "string", description: "Tenant ID (optional)" },
    },
    required: ["appId", "principalId", "principalType", "role"],
  },
};

export const unsharePowerAppTool: Tool = {
  name: "unshare_powerapp",
  description: "Remove a user or group's access to a Power App.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      roleAssignmentId: { type: "string", description: "The role assignment ID to remove" },
    },
    required: ["appId", "roleAssignmentId"],
  },
};

export const listPowerAppsAdminTool: Tool = {
  name: "list_powerapps_admin",
  description: "List all Power Apps in an environment as admin. Requires Power Platform admin role.",
  inputSchema: {
    type: "object",
    properties: {
      environment: { type: "string", description: "Environment name or GUID. Uses the default environment if omitted." },
    },
  },
};

export const getPowerAppAdminTool: Tool = {
  name: "get_powerapp_admin",
  description: "Get Power App details as admin. Requires Power Platform admin role.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      environment: { type: "string", description: "Environment GUID" },
    },
    required: ["appId", "environment"],
  },
};

export const deletePowerAppAdminTool: Tool = {
  name: "delete_powerapp_admin",
  description: "Delete a Power App as admin. Requires Power Platform admin role. Set confirm=true to proceed.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID) to delete" },
      environment: { type: "string", description: "Environment GUID" },
      confirm: { type: "boolean", description: "Set to true to confirm deletion" },
    },
    required: ["appId", "environment"],
  },
};

export const setPowerAppOwnerTool: Tool = {
  name: "set_powerapp_owner",
  description: "Transfer ownership of a Power App to another user. Requires Power Platform admin role.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      environment: { type: "string", description: "Environment GUID" },
      newOwnerId: { type: "string", description: "Entra object ID of the new owner" },
      newOwnerType: {
        type: "string",
        enum: ["User", "ServicePrincipal"],
        description: "Type of new owner (default: User)",
      },
    },
    required: ["appId", "environment", "newOwnerId"],
  },
};

export const quarantinePowerAppTool: Tool = {
  name: "quarantine_powerapp",
  description: "Quarantine or unquarantine a Power App. Quarantined apps cannot be launched. Requires admin role.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: "The app ID (GUID)" },
      environment: { type: "string", description: "Environment GUID" },
      quarantine: { type: "boolean", description: "true to quarantine, false to unquarantine" },
    },
    required: ["appId", "environment", "quarantine"],
  },
};

// ============================================================================
// Zod Schemas
// ============================================================================

const listPowerAppsSchema = z.object({
  environment: z.string().optional(),
});

const guidString = z.string().min(1).max(100);

const appIdSchema = z.object({
  appId: guidString,
});

const deleteAppSchema = z.object({
  appId: guidString,
  confirm: z.boolean().optional().default(false),
});

const restoreVersionSchema = z.object({
  appId: guidString,
  versionId: guidString,
  confirm: z.boolean().optional().default(false),
});

const setDisplayNameSchema = z.object({
  appId: guidString,
  displayName: z.string().min(1).max(500),
});

const shareAppSchema = z.object({
  appId: guidString,
  principalId: guidString,
  principalType: z.enum(["User", "Group", "Tenant", "ServicePrincipal"]),
  role: z.enum(["CanEdit", "CanView"]),
  tenantId: guidString.optional(),
});

const unshareAppSchema = z.object({
  appId: guidString,
  roleAssignmentId: guidString,
});

const adminListSchema = z.object({
  environment: guidString.optional(),
});

const adminAppSchema = z.object({
  appId: guidString,
  environment: guidString,
});

const adminDeleteSchema = z.object({
  appId: guidString,
  environment: guidString,
  confirm: z.boolean().optional().default(false),
});

const setOwnerSchema = z.object({
  appId: guidString,
  environment: guidString,
  newOwnerId: guidString,
  newOwnerType: z.enum(["User", "ServicePrincipal"]).optional().default("User"),
});

const quarantineSchema = z.object({
  appId: guidString,
  environment: guidString,
  quarantine: z.boolean(),
});

// ============================================================================
// Handlers
// ============================================================================

export async function handleListPowerApps(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = listPowerAppsSchema.parse(input);
  logger.info({ input: parsed }, "Executing list_powerapps");

  const apps = await api.listApps(parsed.environment);

  if (apps.length === 0) {
    return "No Power Apps found in this environment.";
  }

  const lines = [`Power Apps (${apps.length}):`];
  for (const app of apps) {
    const modified = new Date(app.properties.lastModifiedTime).toLocaleDateString();
    const owner = app.properties.owner?.displayName ?? app.properties.owner?.email ?? "unknown";
    lines.push(`  ${app.properties.displayName} (${app.name ?? "?"}) - Owner: ${owner} - ${modified}`);
  }
  return lines.join("\n");
}

export async function handleGetPowerApp(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = appIdSchema.parse(input);
  logger.info({ appId: parsed.appId }, "Executing get_powerapp");

  const app = await api.getApp(parsed.appId);
  const p = app.properties;

  const lines = [
    `Name: ${p.displayName}`,
    `ID: ${app.name}`,
    `Type: ${p.appType ?? "Unknown"}`,
    `Owner: ${p.owner?.displayName ?? p.owner?.email ?? "unknown"}`,
    `Created: ${new Date(p.createdTime).toLocaleDateString()}`,
    `Modified: ${new Date(p.lastModifiedTime).toLocaleDateString()}`,
  ];

  if (p.description) lines.push(`Description: ${p.description}`);
  if (p.appPlayUri) lines.push(`Play URL: ${p.appPlayUri}`);
  if (p.sharedUsersCount !== undefined) lines.push(`Shared with: ${p.sharedUsersCount} users, ${p.sharedGroupsCount ?? 0} groups`);

  return lines.join("\n");
}

export async function handleDeletePowerApp(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = deleteAppSchema.parse(input);
  logger.info({ appId: parsed.appId }, "Executing delete_powerapp");

  if (!parsed.confirm) {
    try {
      const app = await api.getApp(parsed.appId);
      throw new ConfirmationRequiredError(`Confirm delete "${app.properties.displayName}"? Set confirm=true to proceed.`);
    } catch (e) {
      if (e instanceof ApiError && e.statusCode === 404) {
        throw new ConfirmationRequiredError(`Confirm delete app ${parsed.appId}? Set confirm=true to proceed.`);
      }
      throw e;
    }
  }

  await api.deleteApp(parsed.appId);
  return `Deleted app ${parsed.appId}`;
}

export async function handlePublishPowerApp(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = appIdSchema.parse(input);
  logger.info({ appId: parsed.appId }, "Executing publish_powerapp");

  await api.publishApp(parsed.appId);
  return `Published app ${parsed.appId}`;
}

export async function handleGetPowerAppVersions(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = appIdSchema.parse(input);
  logger.info({ appId: parsed.appId }, "Executing get_powerapp_versions");

  const versions = await api.getAppVersions(parsed.appId);

  if (versions.length === 0) {
    return "No versions found.";
  }

  const lines = [`Versions (${versions.length}):`];
  for (const v of versions) {
    const created = new Date(v.properties.createdTime).toLocaleDateString();
    lines.push(`  ${v.name} - ${v.properties.lifeCycleId ?? "Draft"} - ${created}`);
  }
  return lines.join("\n");
}

export async function handleRestorePowerAppVersion(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = restoreVersionSchema.parse(input);
  logger.info({ appId: parsed.appId, versionId: parsed.versionId }, "Executing restore_powerapp_version");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm restore app ${parsed.appId} to version ${parsed.versionId}? Set confirm=true to proceed.`);
  }

  await api.restoreAppVersion(parsed.appId, parsed.versionId);
  return `Restored app ${parsed.appId} to version ${parsed.versionId}`;
}

export async function handleSetPowerAppDisplayName(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = setDisplayNameSchema.parse(input);
  logger.info({ appId: parsed.appId, displayName: parsed.displayName }, "Executing set_powerapp_display_name");

  const app = await api.setAppDisplayName(parsed.appId, parsed.displayName);
  return `Renamed app to "${app.properties.displayName}"`;
}

export async function handleGetPowerAppPermissions(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = appIdSchema.parse(input);
  logger.info({ appId: parsed.appId }, "Executing get_powerapp_permissions");

  const permissions = await api.getAppPermissions(parsed.appId);

  if (permissions.length === 0) {
    return "No permissions found.";
  }

  const lines = [`Permissions (${permissions.length}):`];
  for (const p of permissions) {
    const principal = p.properties.principal;
    const name = principal.displayName ?? principal.email ?? principal.id;
    lines.push(`  ${name} (${principal.type}) - ${p.properties.roleName}`);
  }
  return lines.join("\n");
}

export async function handleSharePowerApp(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = shareAppSchema.parse(input);
  logger.info({ appId: parsed.appId, principalId: parsed.principalId }, "Executing share_powerapp");

  await api.shareApp(
    parsed.appId,
    parsed.principalId,
    parsed.principalType,
    parsed.role,
    parsed.tenantId
  );
  return `Shared app with ${parsed.principalType} ${parsed.principalId} as ${parsed.role}`;
}

export async function handleUnsharePowerApp(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = unshareAppSchema.parse(input);
  logger.info({ appId: parsed.appId, roleAssignmentId: parsed.roleAssignmentId }, "Executing unshare_powerapp");

  await api.unshareApp(parsed.appId, parsed.roleAssignmentId);
  return `Removed access for role assignment ${parsed.roleAssignmentId}`;
}

export async function handleListPowerAppsAdmin(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = adminListSchema.parse(input);
  logger.info({ environment: parsed.environment }, "Executing list_powerapps_admin");

  const apps = await api.listAppsAdmin(parsed.environment);

  if (apps.length === 0) {
    return "No Power Apps found in this environment (admin view).";
  }

  const lines = [`Power Apps - Admin View (${apps.length}):`];
  for (const app of apps) {
    const modified = new Date(app.properties.lastModifiedTime).toLocaleDateString();
    const owner = app.properties.owner?.displayName ?? app.properties.owner?.email ?? "unknown";
    lines.push(`  ${app.properties.displayName} (${app.name ?? "?"}) - Owner: ${owner} - ${modified}`);
  }
  return lines.join("\n");
}

export async function handleGetPowerAppAdmin(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = adminAppSchema.parse(input);
  logger.info({ appId: parsed.appId, environment: parsed.environment }, "Executing get_powerapp_admin");

  const app = await api.getAppAdmin(parsed.appId, parsed.environment);
  const p = app.properties;

  const lines = [
    `Name: ${p.displayName}`,
    `ID: ${app.name}`,
    `Type: ${p.appType ?? "Unknown"}`,
    `Owner: ${p.owner?.displayName ?? p.owner?.email ?? "unknown"}`,
    `Created: ${new Date(p.createdTime).toLocaleDateString()}`,
    `Modified: ${new Date(p.lastModifiedTime).toLocaleDateString()}`,
  ];
  if (p.description) lines.push(`Description: ${p.description}`);
  return lines.join("\n");
}

export async function handleDeletePowerAppAdmin(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = adminDeleteSchema.parse(input);
  logger.info({ appId: parsed.appId, environment: parsed.environment }, "Executing delete_powerapp_admin");

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm admin-delete app ${parsed.appId}? Set confirm=true to proceed.`);
  }

  await api.deleteAppAdmin(parsed.appId, parsed.environment);
  return `Deleted app ${parsed.appId} (admin)`;
}

export async function handleSetPowerAppOwner(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = setOwnerSchema.parse(input);
  logger.info({ appId: parsed.appId, newOwnerId: parsed.newOwnerId }, "Executing set_powerapp_owner");

  await api.setAppOwner(parsed.appId, parsed.environment, parsed.newOwnerId, parsed.newOwnerType);
  return `Transferred ownership of app ${parsed.appId} to ${parsed.newOwnerId}`;
}

export async function handleQuarantinePowerApp(api: PowerAppsApi, input: unknown): Promise<string> {
  const parsed = quarantineSchema.parse(input);
  logger.info({ appId: parsed.appId, quarantine: parsed.quarantine }, "Executing quarantine_powerapp");

  await api.quarantineApp(parsed.appId, parsed.environment, parsed.quarantine);
  return parsed.quarantine
    ? `Quarantined app ${parsed.appId}`
    : `Unquarantined app ${parsed.appId}`;
}

// ============================================================================
// All tool definitions array
// ============================================================================

export const powerAppsTools: Tool[] = [
  listPowerAppsTool,
  getPowerAppTool,
  deletePowerAppTool,
  publishPowerAppTool,
  getPowerAppVersionsTool,
  restorePowerAppVersionTool,
  setPowerAppDisplayNameTool,
  getPowerAppPermissionsTool,
  sharePowerAppTool,
  unsharePowerAppTool,
  listPowerAppsAdminTool,
  getPowerAppAdminTool,
  deletePowerAppAdminTool,
  setPowerAppOwnerTool,
  quarantinePowerAppTool,
];
